import React from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import { FiChevronLeft, FiChevronRight, FiMessageSquare } from 'react-icons/fi';
import SEO from '../components/SEO';

const zoomIn = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1.1);
  }
`;

const ServicesContainer = styled.div`
  padding-top: 0;
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, rgba(26, 77, 128, 0.8) 0%, rgba(44, 62, 80, 0.7) 100%), url('/IMG-20250802-WA0073.jpg');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-attachment: fixed;
  color: ${props => props.theme.colors.white};
  padding: 120px 0 80px;
  text-align: center;
  position: relative;
  min-height: 60vh;
  display: flex;
  align-items: center;
  animation: ${zoomIn} 10s ease-in-out infinite;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    background-attachment: scroll;
    min-height: 50vh;
    padding: 100px 0 60px;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const Section = styled.section`
  padding: 80px 0;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 60px 0;
  }
`;

const SectionTitle = styled(motion.h2)`
  text-align: center;
  margin-bottom: 60px;
  color: ${props => props.theme.colors.primary};
  
  &::after {
    content: '';
    display: block;
    width: 80px;
    height: 4px;
    background: ${props => props.theme.colors.secondary};
    margin: 20px auto 0;
    border-radius: 2px;
  }
`;



const OurServicesSection = styled.section`
  background: #f9f8f8;
  padding: 80px 0;
  margin-bottom: 0;
  border-bottom: 1px solid #ccc9c9;
  border-bottom-style: dashed;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 60px 0;
  }
`;

const OurServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 25px;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: 15px;
  }
`;

const OurServiceItem = styled(motion.div)`
  background: ${props => props.theme.colors.white};
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
  transition: all 0.3s ease;
  border: 1px solid rgba(26, 77, 128, 0.1);
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(26, 77, 128, 0.15);
    border-color: ${props => props.theme.colors.secondary};
  }
`;

const OurServiceImage = styled.div`
  height: 160px;
  overflow: hidden;
  position: relative;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }
  
  &:hover img {
    transform: scale(1.08);
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, rgba(26, 77, 128, 0.1), rgba(243, 156, 18, 0.1));
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover::after {
    opacity: 1;
  }
`;

const OurServiceContent = styled.div`
  padding: 16px;
  
  h3 {
    color: ${props => props.theme.colors.primary};
    margin-bottom: 8px;
    font-size: 1.1rem;
    font-weight: 600;
    line-height: 1.3;
    text-transform: capitalize;
  }
  
  p {
    color: #333333;
    line-height: 1.5;
    font-size: 0.9rem;
    font-family: 'Times New Roman', Times, serif;
    margin: 0;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
`;

// Testimonials data
const testimonialsData = [
  {
    id: 1,
    text: "We have purchased liner, frame tents, and pole tents from Trimurthy Tents and have been very pleased with all the above. Most of their customer service and turn-around-time has been better than any other company we have dealt with.",
    author: "Ravi Kishan",
    date: "2 days ago"
  },
  {
    id: 2,
    text: "Amazing party and, as always, everything from the rentals to the service was above and beyond! Thank you, thank you, thank you!!",
    author: "Anil Kumar",
    date: "4 days ago"
  },
  {
    id: 3,
    text: "We have purchased liner, frame tents, and pole tents from Trimurthy Tents and have been very pleased with all the above. Most of their customer service and turn-around-time has been better than any other company we have dealt with.",
    author: "Prathik Jain",
    date: "1 day ago"
  }
];

// Testimonials Carousel Component
const TestimonialsCarousel = () => {
  const [currentIndex, setCurrentIndex] = React.useState(0);

  const nextSlide = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === testimonialsData.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevSlide = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? testimonialsData.length - 1 : prevIndex - 1
    );
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  React.useEffect(() => {
    const interval = setInterval(() => {
      nextSlide();
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div style={{ position: 'relative', maxWidth: '900px', margin: '0 auto' }}>
      {/* Carousel Container */}
      <div style={{ 
        overflow: 'hidden', 
        position: 'relative',
        borderRadius: '0',
        background: 'rgba(255, 255, 255, 0.05)',
        padding: '40px 20px'
      }}>
        <motion.div
          key={currentIndex}
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -50 }}
          transition={{ duration: 0.5 }}
          style={{ textAlign: 'center' }}
        >
          <div style={{ marginBottom: '30px' }}>
            <FiMessageSquare 
              style={{ 
                fontSize: '2rem', 
                color: '#1a4d80', 
                marginBottom: '20px',
                display: 'block',
                margin: '0 auto 20px'
              }} 
            />
            <p style={{
              fontSize: '1.2rem',
              lineHeight: '1.8',
              marginBottom: '30px',
              fontStyle: 'italic',
              color: '#f8f9fa'
            }}>
              {testimonialsData[currentIndex].text}
            </p>
            <FiMessageSquare 
              style={{ 
                fontSize: '2rem', 
                color: '#1a4d80',
                display: 'block',
                margin: '20px auto 0'
              }} 
            />
          </div>
          
          <div style={{ textAlign: 'center' }}>
            <h5 style={{
              fontSize: '1.3rem',
              fontWeight: '600',
              marginBottom: '10px',
              color: '#1a4d80'
            }}>
              {testimonialsData[currentIndex].author}
            </h5>
            <p style={{
              fontSize: '0.9rem',
              color: '#adb5bd',
              margin: '0'
            }}>
              {testimonialsData[currentIndex].date}
            </p>
          </div>
        </motion.div>
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={prevSlide}
        style={{
          position: 'absolute',
          left: '-50px',
          top: '50%',
          transform: 'translateY(-50%)',
          background: '#1a4d80',
          border: 'none',
          color: 'white',
          width: '40px',
          height: '40px',
          borderRadius: '0',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '1.2rem',
          transition: 'all 0.3s ease',
          zIndex: 10
        }}
        onMouseEnter={(e) => {
          e.target.style.background = '#2c5aa0';
          e.target.style.transform = 'translateY(-50%) scale(1.1)';
        }}
        onMouseLeave={(e) => {
          e.target.style.background = '#1a4d80';
          e.target.style.transform = 'translateY(-50%) scale(1)';
        }}
      >
        <FiChevronLeft />
      </button>

      <button
        onClick={nextSlide}
        style={{
          position: 'absolute',
          right: '-50px',
          top: '50%',
          transform: 'translateY(-50%)',
          background: '#1a4d80',
          border: 'none',
          color: 'white',
          width: '40px',
          height: '40px',
          borderRadius: '0',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '1.2rem',
          transition: 'all 0.3s ease',
          zIndex: 10
        }}
        onMouseEnter={(e) => {
          e.target.style.background = '#2c5aa0';
          e.target.style.transform = 'translateY(-50%) scale(1.1)';
        }}
        onMouseLeave={(e) => {
          e.target.style.background = '#1a4d80';
          e.target.style.transform = 'translateY(-50%) scale(1)';
        }}
      >
        <FiChevronRight />
      </button>

      {/* Dots Navigation */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        gap: '10px',
        marginTop: '30px'
      }}>
        {testimonialsData.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            style={{
              width: '12px',
              height: '12px',
              borderRadius: '0',
              border: 'none',
              background: index === currentIndex ? '#1a4d80' : '#6c757d',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              if (index !== currentIndex) {
                e.target.style.background = '#adb5bd';
              }
            }}
            onMouseLeave={(e) => {
              if (index !== currentIndex) {
                e.target.style.background = '#6c757d';
              }
            }}
          />
        ))}
      </div>
    </div>
  );
};

const Services = () => {
  const servicesData = [
    {
      image: '/german_hangers.jpg',
      alt: 'German Hanger / Structure / Tent',
      title: 'German Hanger / Structure / Tent',
      description: 'German Hanger Tent setup for large wedding or exhibition – durable, waterproof, and elegant event structure.'
    },
    {
      image: '/IMG-20250802-WA0072.jpg',
      alt: 'Transparent German hanger',
      title: 'Transparent German hanger',
      description: 'Transparent German Hanger Tent for weddings and events – clear roof structure with premium weatherproof design.'
    },
    {
      image: '/IMG-20250802-WA0073.jpg',
      alt: 'Waterproof Plywood Platforms',
      title: 'Single Seater Leather Sofas for rent ',
      description: 'Wooden plywood platform flooring for events and weddings – strong, level, and weather-resistant stage base.'
    },
    {
      image: '/IMG-20250802-WA0075.jpg',
      alt: 'Air Conditioner of various tonnes',
      title: 'Barricading for rent ',
      description: 'Event air conditioning units of various tons for tents and halls – efficient cooling for weddings, exhibitions, events, & corporate office maintenance'
    },
    {
      image: '/IMG-20250802-WA0018.jpg',
      alt: 'Super structure / Waterproof pendals',
      title: 'Vessels Banquet Chairs for rent',
      description: 'Waterproof super structure pandal for large outdoor events – durable and weatherproof event shelter.'
    },
    {
      image: '/IMG-20250802-WA0076.jpg',
      alt: 'Industrial Shed',
      title: 'Decoration for weddings',
      description: 'Industrial shed tent setup – heavy-duty, weather-resistant structure for warehouses and factory use.'
    },
    {
      image: '/IMG-20250802-WA0077.jpg',
      alt: 'Aluminium Pagodas',
      title: 'LED Lighiting for event ',
      description: 'Imported aluminium pagoda tent setup for weddings and events – stylish, waterproof, and durable structure.'
    },
    {
      image: '/IMG-20250802-WA0078.jpg',
      alt: 'Exhibition Stalls / Octornm Stalls',
      title: 'Mantapa for rent ',
      description: 'Octonorm exhibition stall setup – modular, professional booths for trade shows and expos.'
    },
    {
      image: '/IMG-20250802-WA0079.jpg',
      alt: 'Metal Barricading',
      title: 'Stages made of both wood & steel',
      description: 'Heavy-duty metal barricades for crowd control at events – durable and secure setup.'
    },
    {
      image: '/IMG-20250802-WA0080.jpg',
      alt: 'Bronze Chain Barricading',
      title: 'Arabian Tents for rent ',
      description: 'Bronze chain barricades at wedding venue – decorative and elegant crowd control solution.'
    },
    {
      image: '/IMG-20250802-WA0081.jpg',
      alt: 'Golden Que stands with red ribbons',
      title: 'Chairs for rent/for hire ',
      description: 'Golden queue stands with red ribbons for elegant event organization.'
    },
    {
      image: '/IMG-20250802-WA0082.jpg',
      alt: 'Single seater leather sofas',
      title: 'Single seater Chairs with clothes',
      description: 'Leather single and two-seater sofas for event seating – premium furniture for weddings and VIP lounges.'
    },
    {
      image: '/IMG-20250802-WA0083.jpg',
      alt: 'Two seater leather sofas',
      title: 'Round Tables for rent',
      description: 'Leather single and two-seater sofas for event seating – premium furniture for weddings and VIP lounges.'
    },
    {
      image: '/IMG-20250802-WA0084.jpg',
      alt: 'Banquet chairs',
      title: 'Banquet chairs',
      description: 'Executive banquet chairs with cushioned seating – elegant and comfortable seating for weddings and events.'
    },
    
    
   
    {
      image: '/IMG-20250802-WA0088.jpg',
      alt: 'VIP / VVIP chairs and Maharaja Chairs',
      title: 'Platform & Stages for rent',
      description: 'VIP and VVIP Maharaja chairs on stage – royal throne seating for weddings and special events.'
    },
    {
      image: '/IMG-20250802-WA0089.jpg',
      alt: 'Steel cots and Mattress',
      title: 'Long Tables for rent',
      description: 'Steel cots with mattresses for guest accommodation at events – strong, comfortable, and easy to install.'
    },
    {
      image: '/IMG-20250802-WA0090.jpg',
      alt: 'Synthetic Carpets of required colours',
      title: 'Banners of required colours',
      description: 'Synthetic and printed carpets in custom colors – elegant flooring for weddings, stages, and exhibitions.'
    },
    {
      image: '/IMG-20250802-WA0091.jpg',
      alt: 'Stage',
      title: 'Steel Tents for rent',
      description: 'Custom stage setup with varying heights – durable event platforms for weddings and performances.'
    },
    
    {
      image: '/IMG-20250802-WA0093.jpg',
      alt: 'Teapoys',
      title: 'Iron poles for rent ',
      description: 'Traditional teapoys for cultural events and traditional ceremonies.'
    },
    {
      image: '/IMG-20250802-WA0060.jpg',
      alt: 'LED Walls',
      title: 'Arabian Tents ',
      description: 'LED video wall setup at event – high-resolution digital display for weddings, conferences, and exhibitions.'
    },
    {
      image: '/IMG-20250802-WA0061.jpg',
      alt: 'Podium',
      title: 'Round Tables with Chairs for rent ',
      description: 'Professional podiums for speakers and presentations at events.'
    },
    {
      image: '/IMG-20250802-WA0062.jpg',
      alt: 'Brass lamp',
      title: 'Flower Decorations for events ',
      description: 'Traditional brass lamps for cultural and religious ceremonies.'
    },
    {
      image: '/IMG-20250802-WA0063.jpg',
      alt: 'Generators',
      title: 'Shamiyanas for rent',
      description: 'Silent generator for event power backup – reliable electricity for weddings, exhibitions, and functions.'
    },
    {
      image: '/IMG-20250802-WA0065.jpg',
      alt: 'LED lighting',
      title: 'German Sheds for rent ',
      description: 'Professional LED lighting systems for events and celebrations.'
    },
    {
      image: '/IMG-20250802-WA0066.jpg',
      alt: 'Flag Poles',
      title: 'Flag Poles',
      description: 'Flag poles and flag display systems for events and ceremonies.'
    },
    {
      image: '/IMG-20250802-WA0050.jpg',
      alt: 'Water proof Taurpaullin sheds',
      title: 'Water proof Taurpaullin sheds',
      description: 'Waterproof tarpaulin shed for outdoor events – durable temporary shelter for weddings, exhibitions, and storage.'
    },
    {
      image: '/IMG-20250802-WA0051.jpg',
      alt: 'Ac - Aircoolers on hire',
      title: 'Backdrops for rent ',
      description: 'Air conditioners and air coolers for rent for events and functions.'
    },
    {
      image: '/IMG-20250802-WA0052.jpg',
      alt: 'Banquet halls in devanahalli',
      title: 'Coolers for rent ',
      description: 'Banquet hall in Devanahalli for weddings and events – spacious, well-decorated indoor venue near vijipura, devanahalli & chikkaballapur'
    },
    {
      image: '/IMG-20250802-WA0053.jpg',
      alt: 'Wedding tents on rent',
      title: 'Metal Barricading for rent ',
      description: 'Wedding tent on rent – elegant event tent setup with lighting and décor for outdoor celebrations.'
    }
  ];

  return (
    <ServicesContainer>
      <SEO
        title="German Tents & Event Rentals In Bangalore | Trimurthy Tent House"
        description="Looking for event rentals in Bangalore? Get German tents, canopies, stages, barricades and exhibition setups from Trimurthy Tent House."
        canonical="https://ttheventorganisers.in/services"
      />
      <HeroSection>
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 style={{ color: '#ffffff' }}>Our Services</h1>
            <p style={{ 
              fontSize: '1.5rem', 
              opacity: 0.9, 
              maxWidth: '600px', 
              margin: '20px auto 0',
              color: '#ffffff',
              fontWeight: 600,
              textShadow: '1px 1px 6px rgba(0,0,0,0.5)',
              fontFamily: "'Times New Roman', Times, serif"
            }}>
              Premium Tent Solutions & Event Management Services for Every Celebration
            </p>
          </motion.div>
        </Container>
      </HeroSection>



      <OurServicesSection>
        <Container>
          <SectionTitle
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            Our Services
          </SectionTitle>
          
          

            <OurServicesGrid>
             {servicesData.map((service, index) => (
               <OurServiceItem
                 key={index}
                 initial={{ opacity: 0, y: 30 }}
                 whileInView={{ opacity: 1, y: 0 }}
                 transition={{ duration: 0.6, delay: index * 0.1 }}
                 viewport={{ once: true }}
                 whileHover={{ scale: 1.02 }}
               >
                 <OurServiceImage>
                   <img src={service.image} alt={service.alt} title={service.title} />
                 </OurServiceImage>
                 <OurServiceContent>
                   <h3>{service.title}</h3>
                   <p>{service.description}</p>
                 </OurServiceContent>
               </OurServiceItem>
             ))}
           </OurServicesGrid>

           <motion.div
             initial={{ opacity: 0, y: 30 }}
             whileInView={{ opacity: 1, y: 0 }}
             transition={{ duration: 0.8 }}
             viewport={{ once: true }}
             style={{ marginTop: '60px' }}
           >
                         <p style={{
              fontSize: '20px',
              color: 'maroon',
              textAlign: 'center',
              marginBottom: '30px',
              fontWeight: '600'
            }}>
              Contact us for all your Tent House supplier services requirements, especially our premium German Hangers. We are the leading provider of German Hangers in Bangalore, offering high-quality tent solutions for large-scale events. We have more than 75+ products that we can supply through tent house services for rent / for hire in Bangalore and across Karnataka and India, with German Hangers being our flagship offering for corporate events, exhibitions, and large gatherings.
            </p>

            <div style={{
               overflowX: 'auto',
               background: '#fff',
               borderRadius: '0',
               boxShadow: '0 5px 15px rgba(0,0,0,0.1)',
               border: '1px solid #dee2e6'
             }}>
               <table style={{
                 width: '100%',
                 borderCollapse: 'collapse',
                 marginBottom: '0'
               }}>
                 <tbody>
                   <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                     <td style={{ 
                       padding: '15px', 
                       borderRight: '1px solid #dee2e6',
                       color: '#000000',
                       fontWeight: '500',
                       fontSize: '1rem',
                       fontFamily: "'Times New Roman', Times, serif"
                     }}>steel tubular sheds for warehousing</td>
                     <td style={{ 
                       padding: '15px', 
                       borderRight: '1px solid #dee2e6',
                       color: '#000000',
                       fontWeight: '500',
                       fontSize: '1rem',
                       fontFamily: "'Times New Roman', Times, serif"
                     }}>German Hangers for rent</td>
                     <td style={{ 
                       padding: '15px',
                       color: '#000000',
                       fontWeight: '500',
                       fontSize: '1rem',
                       fontFamily: "'Times New Roman', Times, serif"
                     }}>Provids stages made of both wood and steel</td>
                   </tr>
                   <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                     <td style={{ 
                       padding: '15px', 
                       borderRight: '1px solid #dee2e6',
                       color: '#000000',
                       fontWeight: '500',
                       fontSize: '1rem',
                       fontFamily: "'Times New Roman', Times, serif"
                     }}>Aluminium Tents for rent</td>
                     <td style={{ 
                       padding: '15px', 
                       borderRight: '1px solid #dee2e6',
                       color: '#000000',
                       fontWeight: '500',
                       fontSize: '1rem',
                       fontFamily: "'Times New Roman', Times, serif"
                     }}>Exhibition Stalls for rent</td>
                     <td style={{ 
                       padding: '15px',
                       color: '#000000',
                       fontWeight: '500',
                       fontSize: '1rem',
                       fontFamily: "'Times New Roman', Times, serif"
                     }}>Backdrops for rent / for hire</td>
                   </tr>
                   <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                     <td style={{ 
                       padding: '15px', 
                       borderRight: '1px solid #dee2e6',
                       color: '#000000',
                       fontWeight: '500',
                       fontSize: '1rem',
                       fontFamily: "'Times New Roman', Times, serif"
                     }}>Metal barricading for rent / for hire</td>
                     <td style={{ 
                       padding: '15px', 
                       borderRight: '1px solid #dee2e6',
                       color: '#000000',
                       fontWeight: '500',
                       fontSize: '1rem',
                       fontFamily: "'Times New Roman', Times, serif"
                     }}>Chairs for rent/ for hire</td>
                     <td style={{ 
                       padding: '15px',
                       color: '#000000',
                       fontWeight: '500',
                       fontSize: '1rem',
                       fontFamily: "'Times New Roman', Times, serif"
                     }}>Transparent German Hangers for rent / for hire</td>
                   </tr>
                   <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                     <td style={{ 
                       padding: '15px', 
                       borderRight: '1px solid #dee2e6',
                       color: '#000000',
                       fontWeight: '500',
                       fontSize: '1rem',
                       fontFamily: "'Times New Roman', Times, serif"
                     }}>Shamianas for rent</td>
                     <td style={{ 
                       padding: '15px', 
                       borderRight: '1px solid #dee2e6',
                       color: '#000000',
                       fontWeight: '500',
                       fontSize: '1rem',
                       fontFamily: "'Times New Roman', Times, serif"
                     }}>Shamiyana Tent for rent</td>
                     <td style={{ 
                       padding: '15px',
                       color: '#000000',
                       fontWeight: '500',
                       fontSize: '1rem',
                       fontFamily: "'Times New Roman', Times, serif"
                     }}>Golden Queue Stands with Red Ribbins for rent / for hire</td>
                   </tr>
                   <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                     <td style={{ 
                       padding: '15px', 
                       borderRight: '1px solid #dee2e6',
                       color: '#000000',
                       fontWeight: '500',
                       fontSize: '1rem',
                       fontFamily: "'Times New Roman', Times, serif"
                     }}>Arabian Tents for rent</td>
                     <td style={{ 
                       padding: '15px', 
                       borderRight: '1px solid #dee2e6',
                       color: '#000000',
                       fontWeight: '500',
                       fontSize: '1rem',
                       fontFamily: "'Times New Roman', Times, serif"
                     }}>Aluminium  German Hanger  for rent</td>
                     <td style={{ 
                       padding: '15px',
                       color: '#000000',
                       fontWeight: '500',
                       fontSize: '1rem',
                       fontFamily: "'Times New Roman', Times, serif"
                     }}>Stages & Platforms for rent / for hire</td>
                   </tr>
                   <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                     <td style={{ 
                       padding: '15px', 
                       borderRight: '1px solid #dee2e6',
                       color: '#000000',
                       fontWeight: '500',
                       fontSize: '1rem'
                     }}>Tent House Dealer</td>
                     <td style={{ 
                       padding: '15px', 
                       borderRight: '1px solid #dee2e6',
                       color: '#000000',
                       fontWeight: '500',
                       fontSize: '1rem'
                     }}>Canopies for rent</td>
                     <td style={{ 
                       padding: '15px',
                       color: '#000000',
                       fontWeight: '500',
                       fontSize: '1rem'
                     }}>Plastic Chairs with covers for rent / for hire</td>
                   </tr>
                   <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                     <td style={{ 
                       padding: '15px', 
                       borderRight: '1px solid #dee2e6',
                       color: '#000000',
                       fontWeight: '500',
                       fontSize: '1rem'
                     }}>Furnitures for rent</td>
                     <td style={{ 
                       padding: '15px', 
                       borderRight: '1px solid #dee2e6',
                       color: '#000000',
                       fontWeight: '500',
                       fontSize: '1rem'
                     }}>Super Structures for rent</td>
                     <td style={{ 
                       padding: '15px',
                       color: '#000000',
                       fontWeight: '500',
                       fontSize: '1rem'
                     }}>Plastic Chairs without convers for rent / for hire</td>
                   </tr>
                   <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                     <td style={{ 
                       padding: '15px', 
                       borderRight: '1px solid #dee2e6',
                       color: '#000000',
                       fontWeight: '500',
                       fontSize: '1rem'
                     }}>Mega Hangers for rent</td>
                     <td style={{ 
                       padding: '15px', 
                       borderRight: '1px solid #dee2e6',
                       color: '#000000',
                       fontWeight: '500',
                       fontSize: '1rem'
                     }}>AC Tents for rent</td>
                     <td style={{ 
                       padding: '15px',
                       color: '#000000',
                       fontWeight: '500',
                       fontSize: '1rem'
                     }}>Round Tables with cloth covering for rent / for hire</td>
                   </tr>
                   <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                     <td style={{ 
                       padding: '15px', 
                       borderRight: '1px solid #dee2e6',
                       color: '#000000',
                       fontWeight: '500',
                       fontSize: '1rem'
                     }}>Vessels Banquet Chairs for rent</td>
                     <td style={{ 
                       padding: '15px', 
                       borderRight: '1px solid #dee2e6',
                       color: '#000000',
                       fontWeight: '500',
                       fontSize: '1rem'
                     }}>Mattresses & Cots for rent</td>
                     <td style={{ 
                       padding: '15px',
                       color: '#000000',
                       fontWeight: '500',
                       fontSize: '1rem'
                     }}>Round Tables without cloth covering for rent / for hire</td>
                   </tr>
                   <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                     <td style={{ 
                       padding: '15px', 
                       borderRight: '1px solid #dee2e6',
                       color: '#000000',
                       fontWeight: '500',
                       fontSize: '1rem'
                     }}>Waterproof Sheds for rent</td>
                     <td style={{ 
                       padding: '15px', 
                       borderRight: '1px solid #dee2e6',
                       color: '#000000',
                       fontWeight: '500',
                       fontSize: '1rem'
                     }}>China Tents  for rent / for hire</td>
                     <td style={{ 
                       padding: '15px',
                       color: '#000000',
                       fontWeight: '500',
                       fontSize: '1rem'
                     }}>Long Tables with cloth covering for rent / for hire</td>
                   </tr>
                   <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                     <td style={{ 
                       padding: '15px', 
                       borderRight: '1px solid #dee2e6',
                       color: '#000000',
                       fontWeight: '500',
                       fontSize: '1rem'
                     }}>AC German Hangers for rent / for hire</td>
                     <td style={{ 
                       padding: '15px', 
                       borderRight: '1px solid #dee2e6',
                       color: '#000000',
                       fontWeight: '500',
                       fontSize: '1rem'
                     }}>VIP Chairs for rent / for hire</td>
                     <td style={{ 
                       padding: '15px',
                       color: '#000000',
                       fontWeight: '500',
                       fontSize: '1rem'
                     }}>Long Tables without cloth covering for rent / for hire</td>
                   </tr>
                   <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                     <td style={{ 
                       padding: '15px', 
                       borderRight: '1px solid #dee2e6',
                       color: '#000000',
                       fontWeight: '500',
                       fontSize: '1rem'
                     }}>Air Coolers for rent / for hire</td>
                     <td style={{ 
                       padding: '15px', 
                       borderRight: '1px solid #dee2e6',
                       color: '#000000',
                       fontWeight: '500',
                       fontSize: '1rem'
                     }}>Waterproof Pandals for rent / for hire</td>
                     <td style={{ 
                       padding: '15px',
                       color: '#000000',
                       fontWeight: '500',
                       fontSize: '1rem'
                     }}>Generators and Generator Sets for rent / for hire</td>
                   </tr>
                   <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                     <td style={{ 
                       padding: '15px', 
                       borderRight: '1px solid #dee2e6',
                       color: '#000000',
                       fontWeight: '500',
                       fontSize: '1rem'
                     }}>Industrial Sheds for rent / for hire</td>
                     <td style={{ 
                       padding: '15px', 
                       borderRight: '1px solid #dee2e6',
                       color: '#000000',
                       fontWeight: '500',
                       fontSize: '1rem'
                     }}>Tent House Supplier</td>
                     <td style={{ 
                       padding: '15px',
                       color: '#000000',
                       fontWeight: '500',
                       fontSize: '1rem'
                     }}>Bronze chain barricading for rent / for hire</td>
                   </tr>
                   <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                     <td style={{ 
                       padding: '15px', 
                       borderRight: '1px solid #dee2e6',
                       color: '#000000',
                       fontWeight: '500',
                       fontSize: '1rem'
                     }}>Banquet Chairs for rent / for hire</td>
                     <td style={{ 
                       padding: '15px', 
                       borderRight: '1px solid #dee2e6',
                       color: '#000000',
                       fontWeight: '500',
                       fontSize: '1rem'
                     }}>VVIP Chairs for rent / for hire</td>
                     <td style={{ 
                       padding: '15px',
                       color: '#000000',
                       fontWeight: '500',
                       fontSize: '1rem'
                     }}>Synthetic carpets of various colours for rent / for hire</td>
                   </tr>
                   <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                     <td style={{ 
                       padding: '15px', 
                       borderRight: '1px solid #dee2e6',
                       color: '#000000',
                       fontWeight: '500',
                       fontSize: '1rem'
                     }}>Steel Cots for rent / for hire</td>
                     <td style={{ 
                       padding: '15px', 
                       borderRight: '1px solid #dee2e6',
                       color: '#000000',
                       fontWeight: '500',
                       fontSize: '1rem'
                     }}>Stages for rent / for hire</td>
                     <td style={{ 
                       padding: '15px',
                       color: '#000000',
                       fontWeight: '500',
                       fontSize: '1rem'
                     }}>Two Seater Leather Sofas for rent / for hire</td>
                   </tr>
                   <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                     <td style={{ 
                       padding: '15px', 
                       borderRight: '1px solid #dee2e6',
                       color: '#000000',
                       fontWeight: '500',
                       fontSize: '1rem'
                     }}>Mattresses for rent / for hire</td>
                     <td style={{ 
                       padding: '15px', 
                       borderRight: '1px solid #dee2e6',
                       color: '#000000',
                       fontWeight: '500',
                       fontSize: '1rem'
                     }}>Podiums for rent / for hire</td>
                     <td style={{ 
                       padding: '15px',
                       color: '#000000',
                       fontWeight: '500',
                       fontSize: '1rem'
                     }}>Maharaja Chairs for rent / for hire</td>
                   </tr>
                   <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                     <td style={{ 
                       padding: '15px', 
                       borderRight: '1px solid #dee2e6',
                       color: '#000000',
                       fontWeight: '500',
                       fontSize: '1rem'
                     }}>Teapoys for rent / for hire</td>
                     <td style={{ 
                       padding: '15px', 
                       borderRight: '1px solid #dee2e6',
                       color: '#000000',
                       fontWeight: '500',
                       fontSize: '1rem'
                     }}>Brass Lamps for rent / for hire</td>
                     <td style={{ 
                       padding: '15px',
                       color: '#000000',
                       fontWeight: '500',
                       fontSize: '1rem'
                     }}>Stage antique chairs for rent / for hire</td>
                   </tr>
                   <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                     <td style={{ 
                       padding: '15px', 
                       borderRight: '1px solid #dee2e6',
                       color: '#000000',
                       fontWeight: '500',
                       fontSize: '1rem'
                     }}>LED Walls for rent / for hire</td>
                     <td style={{ 
                       padding: '15px', 
                       borderRight: '1px solid #dee2e6',
                       color: '#000000',
                       fontWeight: '500',
                       fontSize: '1rem'
                     }}>Flag Poles for rent / for hire</td>
                     <td style={{ 
                       padding: '15px',
                       color: '#000000',
                       fontWeight: '500',
                       fontSize: '1rem'
                     }}>LED Lighting for rent / for hire</td>
                   </tr>
                   <tr>
                     <td style={{ 
                       padding: '15px', 
                       borderRight: '1px solid #dee2e6',
                       color: '#000000',
                       fontWeight: '500',
                       fontSize: '1rem'
                     }}>Fans for rent / for hire</td>
                     <td style={{ 
                       padding: '15px', 
                       borderRight: '1px solid #dee2e6',
                       color: '#000000',
                       fontWeight: '500',
                       fontSize: '1rem'
                     }}></td>
                     <td style={{ 
                       padding: '15px',
                       color: '#000000',
                       fontWeight: '500',
                       fontSize: '1rem'
                     }}></td>
                   </tr>
                 </tbody>
               </table>
             </div>


           </motion.div>


        </Container>
      </OurServicesSection>

      {/* Testimonials Section */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
        style={{
          background: '#1b1b1b',
          padding: '80px 0',
          color: 'white'
        }}
      >
        <Container>
          <motion.h3
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            style={{
              textAlign: 'center',
              textTransform: 'uppercase',
              marginBottom: '60px',
              fontSize: '2.5rem',
              fontWeight: '600',
              color: 'white'
            }}
          >
            Testimonials
          </motion.h3>
          
          <TestimonialsCarousel />
        </Container>
      </motion.section>


    </ServicesContainer>
  );
};

export default Services; 


